<template>
  <div>
    <div v-if="deal && showDealActionsRow" class="justify-content-end d-flex mt-2 flex-wrap gap-2">
      <template v-if="hasRole([UserRole.ADMIN])">
        <button
          type="button"
          @click="showRequestLog"
          class="btn btn-outline-secondary btn-sm text-capitalize-first"
        >
          {{ helper.trans('labels.show_request_log') }}
        </button>
        <button
          type="button"
          @click="showWebhooksHistory"
          class="btn btn-outline-secondary btn-sm text-capitalize-firs ms-1"
        >
          {{ helper.trans('labels.show_webhooks_history') }}
        </button>
        <button
          type="button"
          @click="showRequestsTimeline(deal.id)"
          class="btn btn-outline-secondary btn-sm text-capitalize-first ms-1"
        >
          {{ helper.trans('labels.show_requests_timeline') }}
        </button>
        <button
          v-if="canResendWebhook"
          type="button"
          class="btn btn-sm btn-outline-secondary text-capitalize-first ms-1"
          @click.prevent="sendCallback(deal.id)"
        >
          {{ helper.trans('labels.send_webhook') }}
        </button>
      </template>
      <template v-else-if="canOperatorResendWebhook">
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary text-capitalize-first"
          @click.prevent="sendCallback(deal.id)"
        >
          {{ helper.trans('labels.send_webhook') }}
        </button>
      </template>
      <button
        v-if="deal.paymentOption === PaymentOption.MANUAL_ECOM"
        type="button"
        @click="showClientCard"
        class="btn btn-outline-primary btn-sm text-capitalize-first"
      >
        {{ helper.trans('labels.show_client_card') }}
      </button>
    </div>

    <table class="table table-striped border-light">
      <thead>
      <tr>
        <th colspan="2">
          <h5 class="mb-0 text-center text-capitalize-first">
            {{helper.trans('labels.trade_deal')}}
          </h5>
        </th>
      </tr>
      </thead>
      <tbody>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.id') }}</th>
        <td v-if="isProvider">
          {{ deal.id }} <copy-button :value="deal?.id"></copy-button>
          <div  v-if="deal?.aggregatorExternalId">
            <small class="text-muted">{{deal.aggregatorExternalId }} <copy-button :value="deal.aggregatorExternalId"></copy-button></small>
          </div>
        </td>
        <td v-else>{{ deal.publicId }} <copy-button :value="deal?.publicId"></copy-button></td>
      </tr>
      <tr v-if="deal.transactionId">
        <th class="text-capitalize-first">{{ helper.trans('labels.transaction') }}</th>
        <td>{{ deal.transactionId }} <copy-button :value="deal.transactionId"></copy-button></td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.type') }}</th>
        <td> {{ helper.trans(`direction.${deal.type}`) }}</td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.date') }}</th>
        <td>{{ dateHelper.format(deal.createdAt) }}</td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.status') }}</th>
        <td>
          {{ helper.trans(`deal_status.${deal.status}`) }}
          <div>
            <template v-for="attachment in deal.attachments" :key="attachment.id">
              <a :href="`${api.baseUrl()}/attachments/${attachment.file}`" target="_blank" type="button" class="me-2 text-warning"><i class="fa fa-paperclip"></i></a>
              <small v-if="chequeCheckerEnabled && receiptVerdict(deal, attachment.id)" class="me-2">{{ receiptVerdict(deal, attachment.id) }}</small>
            </template>
          </div>
        </td>
      </tr>
      <tr v-if="deal.disputeResult">
        <th class="text-capitalize-first">{{ helper.trans('labels.dispute_result') }}</th>
        <td>
          <span class="text-primary">{{ helper.trans(`dispute_result.${deal.disputeResult}`) }}</span>
        </td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans(deal.merchantMakerSum ? 'labels.trader_rate_merchant_rate' : 'labels.rate') }}</th>
        <td>{{ numberFormatter.formatNumber(deal.takerSum.amount / deal.makerSum.amount, deal.takerSum.subunit) }}<template v-if="deal.merchantMakerSum"> / {{ numberFormatter.formatNumber(deal.takerSum.amount / deal.merchantMakerSum.amount, deal.takerSum.subunit) }}</template> {{ deal.takerSum.currency }}</td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.in_sum') }}</th>
        <td>
          <div>
            <span v-if="deal.actualTakerSum">
              {{ numberFormatter.formatMoney(deal.actualTakerSum) }}
              <copy-button :value="deal.actualTakerSum.amount"></copy-button>
            </span>
            <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
              {{ numberFormatter.formatMoney(deal.takerSum) }}
              <copy-button :value="deal.takerSum.amount"></copy-button>
            </span>
            <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1">/</span>
            <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1 small text-secondary">
              <span v-if="deal.actualMakerSum">
                {{ numberFormatter.formatMoney(deal.actualMakerSum) }}
                <copy-button :value="deal.actualMakerSum.amount"></copy-button>
              </span>
              <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                {{ numberFormatter.formatMoney(deal.makerSum) }}
                <copy-button :value="deal.makerSum.amount"></copy-button>
              </span>
            </span>
          </div>
        </td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.out_sum') }}</th>
        <td :class="{'text-danger': deal.type === Direction.PAY_IN, 'text-success': deal.type === Direction.PAY_OUT}">
          <div>
            <span v-if="deal.actualMakerSum">
              {{deal.type === Direction.PAY_IN ? '-' : '+'}}{{ numberFormatter.formatMoney(deal.actualMakerSum) }}
              <copy-button :value="deal.actualMakerSum.amount"></copy-button>
            </span>
            <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
              {{deal.type === Direction.PAY_IN ? '-' : '+'}}{{ numberFormatter.formatMoney(deal.makerSum) }}
              <copy-button :value="deal.makerSum.amount"></copy-button>
            </span>
            <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1">/</span>
            <span v-if="deal.takerSum.currency !== deal.makerSum.currency" class="ms-1 small text-secondary">
              <span v-if="deal.actualTakerSum">
                {{ numberFormatter.formatMoney(deal.actualTakerSum) }}
                <copy-button :value="deal.actualTakerSum.amount"></copy-button>
              </span>
              <span v-else :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
                {{ numberFormatter.formatMoney(deal.takerSum) }}
                <copy-button :value="deal.takerSum.amount"></copy-button>
              </span>
            </span>
          </div>
        </td>
      </tr>
      <tr v-if="deal.rewardSum">
        <th class="text-capitalize-first">{{ helper.trans('labels.reward_sum') }}</th>
        <td :class="parseFloat(deal.rewardSum.amount) >= 0 ? 'text-success' : 'text-danger'">
          <div v-if="deal.actualRewardSum">
            {{ numberFormatter.formatMoney(deal.actualRewardSum) }}
            <copy-button :value="deal.actualRewardSum.amount"></copy-button>
          </div>
          <div v-if="!deal.actualRewardSum || deal.isActualSumsChanged" :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
            {{ numberFormatter.formatMoney(deal.rewardSum) }}
            <copy-button :value="deal.rewardSum.amount"></copy-button>
          </div>
        </td>
      </tr>
      <tr v-if="deal.refRewardSum && hasRole([UserRole.ADMIN, UserRole.TRADER_TEAM_LEAD, UserRole.OPERATOR])">
        <th class="text-capitalize-first">{{ helper.trans('labels.ref_reward_sum') }}</th>
        <td class="text-success">
          <div v-if="deal.actualRefRewardSum">
            +{{ numberFormatter.formatMoney(deal.actualRefRewardSum) }}
            <copy-button :value="deal.actualRefRewardSum.amount"></copy-button>
          </div>
          <div v-if="!deal.actualRefRewardSum || deal.isActualSumsChanged" :class="{'text-decoration-line-through': deal.isActualSumsChanged}">
            +{{ numberFormatter.formatMoney(deal.refRewardSum) }}
            <copy-button :value="deal.refRewardSum.amount"></copy-button>
          </div>
        </td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.buyer') }}</th>
        <td style="word-break: break-all; max-width: 300px">
          {{ deal.takerId ?? 'n/a' }}
          <copy-button v-if="deal.takerId" :value="deal.takerId"></copy-button>
        </td>
      </tr>
      <tr>
        <th class="text-capitalize-first">{{ helper.trans('labels.requisites') }}</th>
        <td>
          <div class="d-flex flex-row align-items-start gap-2">
            <PayMethodIcon :method="deal.paymentMethod" class="align-self-center"></PayMethodIcon>
            <div class="d-flex flex-column flex-grow-1">
              <small class=""> {{ helper.trans('payment_options.' + deal.paymentOption) }}</small>
              <span>
                <template v-if="deal.paymentOption === PaymentOption.MANUAL_ECOM || deal.paymentOption === PaymentOption.TO_CARD">
                  <span>{{ formatCardRequisites(deal.paymentRequisites.requisites) }}</span>
                  <copy-button :value="formatCardRequisites(deal.paymentRequisites.requisites)" class="ms-1"></copy-button>
                </template>
                <small v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR && !helper.dealQrCodeLink(deal)" class="text-secondary">{{ helper.trans('labels.manual_sbp_qr_awaiting_link') }}</small>
                <template v-else-if="!helper.isEmvQrOption(deal.paymentOption) && (!!deal.paymentRequisites.requisites || (deal.paymentOption === PaymentOption.MANUAL_SBP_QR && helper.dealQrCodeLink(deal)))">
                  <a v-if="deal.paymentOption === PaymentOption.SBP_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                  <a v-else-if="deal.paymentOption === PaymentOption.MANUAL_SBP_QR && helper.dealQrCodeLink(deal)" :href="helper.dealQrCodeLink(deal)" target="_blank">{{ helper.domain(helper.dealQrCodeLink(deal)) }}</a>
                  <a v-else-if="deal.paymentOption === PaymentOption.CLICK_UZ_QR && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.trans('labels.my_click_uz') }}</a>
                  <a v-else-if="deal.paymentOption === PaymentOption.TIPS && deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                  <a v-else-if="deal.paymentRequisites.requisites?.startsWith('http')" :href="deal.paymentRequisites.requisites" target="_blank">{{ helper.domain(deal.paymentRequisites.requisites) }}</a>
                  <span v-else>{{ deal.paymentRequisites.requisites }}</span>
                  <copy-button :value="deal.paymentOption === PaymentOption.MANUAL_SBP_QR && helper.dealQrCodeLink(deal) ? helper.dealQrCodeLink(deal) : deal.paymentRequisites.requisites" class="ms-1"></copy-button>
                </template>
              </span>
              <div v-if="helper.isEmvQrOption(deal.paymentOption)">
                <div>
                  <vue-qrcode
                              :value="helper.emvQrData(deal)"
                              tag="svg"
                              class="viet-qr-payment__qr"
                              style="width: 100%; border-radius: 10px"
                              :options="{ width: '200px' }"/>
                </div>
                <div>
                  <small class="text-secondary text-capitalize-first me-2">{{ helper.trans('labels.raw_field.qrString') }}</small>
                  <copy-button :value="helper.emvQrData(deal)"></copy-button>
                </div>
              </div>
              <ManualSbpQrLinkEditor bootstrap-modal :deal="deal" @applied="$emit('manual-sbp-qr-updated', $event)" />
              <small v-if="deal.paymentOption === PaymentOption.SBP_QR && deal.requisites && !deal.requisites.requisites.startsWith('http')" class="text-secondary"> {{ deal.requisites.requisites }}</small>
              <small v-else class="text-secondary"> {{ deal.paymentRequisites.holder }}</small>
            </div>

          </div>
        </td>
      </tr>
      <tr v-if="deal.paymentOption === PaymentOption.MANUAL_ECOM && deal.manualEcom3dsCode">
        <th class="text-capitalize-first">{{ helper.trans('labels.manual_ecom_3ds_code') }}</th>
        <td>{{ deal.manualEcom3dsCode }}</td>
      </tr>
      <tr v-if="(hasRole([UserRole.ADMIN, UserRole.OPERATOR])) && deal.timeSpentCreateDeal !== null && deal.timeSpentCreateDeal !== undefined">
        <th class="text-capitalize-first">{{ helper.trans('labels.time_spent_create_deal') }}</th>
        <td>{{ deal.timeSpentCreateDeal.toFixed(3) }} сек.</td>
      </tr>
      </tbody>
    </table>

    <MatchingPayInDealsDetailsTable v-if="deal.matchingPayoutDeal && deal.type === Direction.PAY_OUT" :deal-id="deal.matchingPayoutDeal.dealId"></MatchingPayInDealsDetailsTable>
    <TransferTransactionDetailsTable v-if="deal.transferTransaction" :transaction="deal.transferTransaction"/>
    <ExternalNSPKTransactionDetailsTable v-if="deal.externalNspkTransaction" :transaction="deal.externalNspkTransaction"/>

    <div v-if="hasRole([UserRole.ADMIN, UserRole.OPERATOR, UserRole.TRADER, UserRole.TRADER_OPERATOR]) && dealHistoriesResponse.items.length > 0" class="overflow-auto">
      <table class="table table-striped border-light">
        <thead>
        <tr>
          <th colspan="5" class="text-center">
            <span class="mb-0 text-center text-capitalize-first">{{ helper.trans('labels.history')}}</span>
          </th>

        </tr>
        <tr>
          <th class="text-capitalize">{{ helper.trans('labels.date')}}</th>
          <th class="text-capitalize">{{ helper.trans('labels.status')}}</th>
          <th class="text-capitalize" v-if="!(isTrader || isTraderOperator)">{{ helper.trans('labels.trader')}}</th>
          <th class="text-capitalize" colspan="2">{{ helper.trans('labels.initiator')}}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="history in dealHistoriesResponse.items" :key="history.id">
          <td class="text-nowrap">{{ dateHelper.format(history.date) }}</td>
          <td>
            <div class="d-flex flex-column">
              <div>
                <span v-if="history.action">{{ helper.trans(`deal_history_action.${history.action}`) }}</span>
                <span v-else>{{ helper.trans(`deal_status.${history.status}`) }}</span>
                <a v-if="!history.action && history.disputeAttachment" :href="`${api.baseUrl()}/attachments/${history.disputeAttachment}`" target="_blank" type="button" class="me-2 text-warning"><i class="fa fa-paperclip"></i></a>
              </div>
              <small v-if="history.action === 'AMOUNT_CHANGED' && history.previousAmount && history.newAmount" class="text-muted">
                {{ history.previousAmount }} -> {{ history.newAmount }} {{ history.amountCurrency || '' }}
              </small>
              <small v-if="history.action === 'DISPUTE_AUTOCLOSE_PAUSED' && history.suspendDisputeAutocloseAt" class="text-muted">
                {{ helper.trans('labels.pause_until', {date: dateHelper.format(history.suspendDisputeAutocloseAt), comment: history.suspendDisputeComment || ''}) }}
              </small>

              <span v-if="!history.action && (history.disputeReason === DisputeReason.INVALID_SUM || history.disputeReason === DisputeReason.PART_PAYOUT)" class="text-danger">
                        (<span class="text-lowercase">{{ helper.trans(`dispute_reason.${history.disputeReason}`) }} </span>: {{numberFormatter.formatNumber(history.disputeReasonData.amount, 2)}} {{deal.takerSum.currency}})
                        </span>
              <ReceiptCheckStatus v-else-if="!history.action && isPayOutReceiptCheck(deal.type, history.disputeReason)" :reason="history.disputeReason" :data="history.disputeReasonData"></ReceiptCheckStatus>
              <span v-else-if="!history.action && history.disputeReason" class="text-lowercase text-danger">
                        ({{ helper.trans(`dispute_reason.${history.disputeReason}`)}})
                        </span>
            </div>

          </td>
          <td v-if="!(isTrader || isTraderOperator)">
            <UserNameLabel v-if="history.trader" :user="history.trader"></UserNameLabel>
            <span v-else>n/a</span>
          </td>
          <td v-if="(isTrader || isTraderOperator)" class="">
            <small v-if="history.initiatorRole" class="text-capitalize-first d-inline-block">{{ helper.trans(`user_roles.${history.initiatorRole}`) }}</small>
            <small v-else>{{ helper.trans(`initiator_type.${history.initiatorType}`) }}</small>
          </td>
          <td v-if="!(isTrader || isTraderOperator)">
            <div class="d-flex flex-column">
              <small>{{ helper.trans(`initiator_type.${history.initiatorType}`) }}</small>
            </div>
          </td>
          <td v-if="!(isTrader || isTraderOperator)">
            <div class="d-flex flex-column">
              <UserNameLabel show-badge v-if="history.initiator" :user="history.initiator"></UserNameLabel>
            </div>
          </td>
        </tr>
        </tbody>
      </table>

      <b-pagination
        v-if="dealHistoriesResponse.totalCount > dealHistoriesResponse.items.length"
        v-model="historyPage"
        :total-rows="dealHistoriesResponse.totalCount"
        :per-page="dealHistoriesResponse.limit"
        :limit="20"
        @update:modelValue="loadHistory()"
        prev-text="Back"
        next-text="Next"
        align="start"
        first-number
        hide-goto-end-buttons
        hide-ellipsis
      ></b-pagination>
    </div>
    <b-modal
      :title="helper.trans('labels.client_card')"
      :modelValue="clientCardData !== null"
      footer-class="d-flex justify-content-end"
      @update:modelValue="clientCardData = null"
    >
      <template #default v-if="clientCardData">
        <div class="d-flex flex-column gap-2">
          <div class="row">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('labels.card_number') }}</label>
            <div class="col-sm-8">{{ clientCardData.cardNumber }}</div>
          </div>
          <div class="row">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('labels.expiry') }}</label>
            <div class="col-sm-8">{{ clientCardData.expiryMonth }}/{{ clientCardData.expiryYear }}</div>
          </div>
          <div class="row">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('labels.cardholder') }}</label>
            <div class="col-sm-8">{{ clientCardData.cardholderName }}</div>
          </div>
          <div class="row">
            <label class="col-sm-4 col-form-label text-capitalize-first">{{ helper.trans('labels.cvv') }}</label>
            <div class="col-sm-8">{{ clientCardData.cvv }}</div>
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="btn btn-outline-dark btn-sm text-capitalize-first" @click="clientCardData = null">
          {{ helper.trans('button.ok') }}
        </button>
      </template>
    </b-modal>
    <InvoiceRequestsTimelineModal ref="invoiceRequestsTimelineModal"></InvoiceRequestsTimelineModal>
    <WebhookHistoryModal ref="webhookHistoryModal"></WebhookHistoryModal>
    <MerchantRequestLogModal ref="merchantRequestLogModal"></MerchantRequestLogModal>
  </div>
</template>

<script lang="ts">

import {computed, defineComponent, onMounted, Prop, PropType, reactive, ref, toRefs} from 'vue'
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import {dateHelper} from "@/service/DateHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import CopyButton from "@/components/CopyButton.vue";
import TraderDealDTO from "@/models/TraderDealDTO";
import {Direction} from "@/models/enum/Direction";
import {useStore} from "vuex";
import {UserRole} from "@/models/enum/UserRole";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import DealHistoryDTO from "@/models/DealHistoryDTO";
import {InitiatorType} from "@/models/enum/InitiatorType";
import PaginationDTO from "@/models/PaginationDTO";
import {Dictionary} from "@/types";
import UserDTO from "@/models/UserDTO";
import UserNameLabel from "@/components/UserNameLabel.vue";
import {DisputeReason, isPayOutReceiptCheck} from "@/models/enum/DisputeReason";
import ReceiptCheckStatus from "@/components/ReceiptCheckStatus.vue";
import {DealStatus} from "@/models/enum/DealStatus";
import {UserPermission} from "@/models/enum/UserPermission";
import {useNotification} from "@kyvg/vue3-notification";
import PayMethodIcon from "@/components/PayMethodIcon.vue";
import {PaymentOption} from "@/models/enum/PaymentOption";
import InvoiceRequestsTimelineModal from "@/components/InvoiceRequestsTimelineModal.vue";
import ClientCardDTO from "@/models/ClientCardDTO";
import TransferTransactionDetailsTable from "@/components/TransferTransactionDetailsTable.vue";
import ExternalNSPKTransactionDetailsTable from "@/components/ExternalNSPKTransactionDetailsTable.vue";
import ManualSbpQrLinkEditor from "@/apps/public/components/ManualSbpQrLinkEditor.vue";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {BModal} from "bootstrap-vue-3";
import WebhookHistoryModal from "@/components/WebhookHistoryModal.vue";
import MerchantRequestLogModal from "@/components/MerchantRequestLogModal.vue";
import MatchingPayInDealsDetailsTable from "@/components/MatchingPayInDealsDetailsTable.vue";
import {receiptVerdict} from "@/service/ReceiptCheckHelper";

const api = new APIClient();

export default defineComponent({
  name: "TradeDealDetailsTable",
  emits: ["manual-sbp-qr-updated"],
  components: {
    ReceiptCheckStatus,
    MatchingPayInDealsDetailsTable,
    MerchantRequestLogModal,
    LoaderWrapper,
    BModal,
    ManualSbpQrLinkEditor,
    ExternalNSPKTransactionDetailsTable,
    TransferTransactionDetailsTable,
    InvoiceRequestsTimelineModal,
    PayMethodIcon,
    CopyButton,
    UserNameLabel,
    WebhookHistoryModal
  },
  props: {
    deal: {
      type: Object as PropType<TraderDealDTO>,
      default: null
    } as Prop<TraderDealDTO>
  },
  setup(props) {
    const webhookHistoryModal = ref<InstanceType<typeof WebhookHistoryModal> | null>(null)
    const merchantRequestLogModal = ref<InstanceType<typeof MerchantRequestLogModal> | null>(null)
    const invoiceRequestsTimelineModal = ref<InstanceType<typeof InvoiceRequestsTimelineModal> | null>(null);
    const clientCardData = ref<ClientCardDTO | null>(null);

    const notification = useNotification()
    const store = useStore();

    const canOperatorResendWebhook = computed(() => {
      const d = props.deal
      if (!d) {
        return false
      }
      return (
        store.getters.isOperator &&
        store.getters.hasPermission(UserPermission.RESEND_COMPLETED_DEAL_WEBHOOK) &&
        [DealStatus.COMPLETED, DealStatus.CANCELED].includes(d.status)
      )
    })

    const canResendWebhook = computed(() => {
      const d = props.deal
      if (!d) {
        return false
      }

      return [DealStatus.COMPLETED, DealStatus.CANCELED].includes(d.status)
    })

    const chequeCheckerEnabled = computed(() => !!store.getters.getAppData?.CHEQUE_WALES_CHECKER_ENABLED)

    const showDealActionsRow = computed(() => {
      const d = props.deal
      if (!d) {
        return false
      }
      if (store.getters.hasRole([UserRole.ADMIN])) {
        return true
      }
      if (canOperatorResendWebhook.value) {
        return true
      }
      return (
        d.paymentOption === PaymentOption.MANUAL_ECOM &&
        store.getters.hasRole([UserRole.ADMIN, UserRole.OPERATOR, UserRole.TRADER, UserRole.TRADER_OPERATOR])
      )
    })

    const formatCardRequisites = (requisites: string) => {
      return requisites.replace(/(\d{4})/g, '$1 ').trim();
    };

    const showClientCard = async () => {
      if (!props.deal) return;
      try {
        clientCardData.value = await api.dealClientCard(props.deal.id);
      } catch (e) {
        notification.notify({ type: 'error', title: helper.trans('server_errors.INTERNAL_ERROR') });
      }
    };

    const state = reactive({
      historyPage: 1,
      dealHistoriesResponse: {
        limit: 20,
        offset: 0,
        totalCount: 0,
        items: []
      } as PaginatedResponseDTO<DealHistoryDTO>
    });

    const loadHistory = async () => {
      if (!props.deal) {
        return;
      }

      state.dealHistoriesResponse.offset = state.dealHistoriesResponse.limit * (state.historyPage - 1)
      state.dealHistoriesResponse = await api.dealHistory(props.deal.id, state.dealHistoriesResponse);
      if (!(store.getters.isTrader || store.getters.isTraderOperator)) {
        let usersIds = [] as string[];
        for (const item of state.dealHistoriesResponse.items) {
          if (item.traderId && !usersIds.includes(item.traderId.toString())) {
            usersIds.push(item.traderId.toString())
          }

          if (item.initiatorType === InitiatorType.USER && !usersIds.includes(item.initiatorId)) {
            usersIds.push(item.initiatorId)
          }
        }

        const users = (await api.users({limit: usersIds.length, offset:0 } as PaginationDTO, {ids: usersIds})).items;
        let userByIds = {} as Dictionary<UserDTO>
        for (const user of users) {
          userByIds[user.id] = user
        }

        for (const item of state.dealHistoriesResponse.items) {
          if (item.traderId) {
            item.trader = userByIds[item.traderId] ?? null;
          }

          if (item.initiatorType === InitiatorType.USER) {
            item.initiator = userByIds[parseInt(item.initiatorId)] ?? null
          }
        }
      }

    }

    const showWebhooksHistory = async () => {
      webhookHistoryModal.value?.show(props.deal?.id!)
    }

    const showRequestLog = async () => {
      merchantRequestLogModal.value?.show(props.deal?.id!)
    }

    const sendCallback = async (id: string) => {
      try {
        await api.dealSendWebhook(id);
        notification.notify({title: helper.trans(`labels.webhook_sent`)});
      } catch (e) {
        if (helper.isValidationErrors(e)) {
          for (const item of helper.getValidationErrors(e)) {
            notification.notify({
              type: 'error',
              title: helper.trans(`server_errors.${item.code}`, {}, item.message)
            })
          }
        } else {
          notification.notify({type: 'error', title: helper.trans(`server_errors.INTERNAL_ERROR`)});
        }
      }
    }

    const showRequestsTimeline = async (invoiceId: string) => {
      invoiceRequestsTimelineModal.value?.show(invoiceId)
    }

    onMounted(async () => {
      if (store.getters.hasRole([UserRole.ADMIN, UserRole.OPERATOR, UserRole.TRADER, UserRole.TRADER_OPERATOR])) {
        await loadHistory()
      }
    })

    return {
      ...toRefs(state),
      dateHelper,
      numberFormatter,
      helper,
      Direction,
      api,
      hasRole: store.getters.hasRole,
      showDealActionsRow,
      canOperatorResendWebhook,
      canResendWebhook,
      chequeCheckerEnabled,
      receiptVerdict,
      isTrader: computed(() => store.getters.isTrader),
      isTraderOperator: computed(() => store.getters.isTraderOperator),
      isProvider: computed(() => store.getters.isProvider),
      UserRole,
      showRequestsTimeline,
      loadHistory,
      DisputeReason,
      isPayOutReceiptCheck,
      sendCallback,
      DealStatus,
      PaymentOption,
      invoiceRequestsTimelineModal,
      clientCardData,
      showWebhooksHistory,
      formatCardRequisites,
      showClientCard,
      webhookHistoryModal,
      showRequestLog,
      merchantRequestLogModal
    }
  }
})
</script>
